import { useTranslation } from 'react-i18next';

import { ARTWORKS, HERO_MODES, SLIDE_INTERVALS, pickSingle } from '../utils/heroArtwork';
import { resolvePhotoUrl } from '../utils/photoUrl';

/*
 * Choosing what the dashboard hero shows.
 *
 * This is chrome, not artwork, so it is a separate file from
 * DashboardHeroArtwork.jsx -- which imports nothing and must keep it that way.
 *
 * It still uses colour literals rather than theme tokens, for the same reason
 * the artwork does: it is drawn ON the hero card, which is a dark green
 * gradient in all three themes. A `--c-text-fg` here would be near-black in
 * lab_light and invisible. ThemePicker has the same problem on the mobile menu
 * and solves it the same way, with its `inverse` prop.
 *
 * Every option carries its own words. A row of little pictures would identify
 * each choice by appearance alone, which is exactly what someone who cannot
 * make out the 0.15-opacity illustration cannot use. (The single-image choice
 * shows each photograph, and names it too.)
 *
 * With `mode` given (Home, 2026-10-18) it is "Home display": the animation
 * (the artwork list below, as before, and the default), a rotating gallery of
 * the bank photographs curators chose, or one of them. Without `mode` it is
 * the artwork list alone, as it always was.
 *
 *   value, onChange                 the animation's artwork
 *   mode, onModeChange              'animation' | 'slideshow' | 'single'
 *   interval, onIntervalChange      the gallery's seconds per photograph
 *   slides, slidesState             GET /home/slides; 'idle'|'loading'|'ready'|'failed'
 *   singleId, onSingleChange        the photograph 'single' shows
 *   onOpen                          the picker was opened (load the slides then)
 *   onManage                        opens the curators' page, for those allowed it
 */
export default function HeroArtworkPicker({
  value, onChange, mode, onModeChange, interval, onIntervalChange,
  slides = [], slidesState = 'idle', singleId, onSingleChange, onOpen, onManage,
}) {
  const { t, i18n } = useTranslation('theme');
  const withModes = mode !== undefined;
  const title = withModes ? t('display.title') : t('artwork.title');
  const photos = slidesState === 'ready' && slides.length > 0;
  const chosen = withModes && mode === 'single' ? pickSingle(slides, singleId) : null;

  return (
    <details
      data-testid="hero-artwork-picker"
      style={{ position: 'absolute', bottom: 10, insetInlineStart: 14, zIndex: 2 }}
      onToggle={(e) => { if (e.currentTarget.open) onOpen?.(); }}
    >
      <summary
        aria-label={title}
        title={withModes ? t('display.tip') : t('artwork.tip')}
        style={{
          listStyle: 'none', cursor: 'pointer', userSelect: 'none',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '3px 9px', borderRadius: 999, fontSize: 11, lineHeight: 1.6,
          color: 'rgba(255,255,255,0.75)',
          border: '1px solid rgba(255,255,255,0.28)',
          background: 'rgba(0,0,0,0.18)',
        }}
      >
        <span aria-hidden="true">✦</span>
        <span>{title}</span>
      </summary>

      {/* With the modes it is taller than the hero above it: it opens downward,
          over the page, rather than up over the nav bar. */}
      <div style={withModes ? { ...panelStyle, ...openDownward } : panelStyle}>
        {withModes && (
          <Group label={t('display.title')} testId="hero-display-modes">
            {HERO_MODES.map((option) => {
              const needsPhotos = option !== 'animation';
              return (
                <Option key={option} selected={mode === option} disabled={needsPhotos && !photos}
                  onSelect={() => onModeChange?.(option)} tip={t(`display.modeTip.${option}`)}
                  testId={`hero-mode-${option}`}>
                  {t(`display.mode.${option}`)}
                </Option>
              );
            })}
            {slidesState === 'loading' && <Note>{t('display.loading')}</Note>}
            {slidesState === 'failed' && <Note>{t('display.failed')}</Note>}
            {slidesState === 'ready' && !slides.length && <Note>{t('display.noSlides')}</Note>}
            {onManage && (
              <button type="button" onClick={onManage} style={linkStyle} data-testid="hero-manage-slides">
                {t('display.manage')}
              </button>
            )}
          </Group>
        )}

        {(!withModes || mode === 'animation' || !photos) && (
          <Group label={t('artwork.title')} divided={withModes}>
            {ARTWORKS.map((option) => (
              <Option key={option} selected={value === option} onSelect={() => onChange(option)}
                tip={t(`artwork.description.${option}`)}>
                {t(`artwork.option.${option}`)}
              </Option>
            ))}
          </Group>
        )}

        {withModes && photos && mode === 'slideshow' && (
          <Group label={t('display.interval')} divided testId="hero-intervals" row>
            {SLIDE_INTERVALS.map((seconds) => (
              <Option key={seconds} selected={interval === seconds} onSelect={() => onIntervalChange?.(seconds)}
                tip={t('display.intervalTip', { count: seconds })} testId={`hero-interval-${seconds}`}>
                {t('display.seconds', { count: seconds })}
              </Option>
            ))}
          </Group>
        )}

        {withModes && photos && mode === 'single' && (
          <Group label={t('display.choosePhoto')} divided testId="hero-single-photos" grid>
            {slides.map((slide) => {
              const words = (i18n?.language === 'he' ? slide.caption_he : slide.caption_en)
                || slide.caption_en || slide.caption_he || slide.taxon?.name || t('display.photoAlt');
              return (
                <button key={slide.slide_id} type="button" role="radio"
                  aria-checked={chosen?.slide_id === slide.slide_id} aria-label={words} title={words}
                  onClick={() => onSingleChange?.(slide.slide_id)} data-testid={`hero-single-${slide.slide_id}`}
                  style={{
                    padding: 0, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', aspectRatio: '4 / 3',
                    border: `2px solid ${chosen?.slide_id === slide.slide_id ? 'rgba(255,255,255,0.9)' : 'transparent'}`,
                    background: 'rgba(255,255,255,0.08)', position: 'relative',
                  }}>
                  <img src={resolvePhotoUrl(slide.image_small_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  {slide.featured && (
                    <span style={featuredStyle}>{t('display.featured')}</span>
                  )}
                </button>
              );
            })}
          </Group>
        )}
      </div>
    </details>
  );
}

function Group({ label, divided = false, row = false, grid = false, testId, children }) {
  return (
    <div role="radiogroup" aria-label={label} data-testid={testId}
      style={{
        ...(divided ? { borderBlockStart: '1px solid rgba(255,255,255,0.18)', marginBlockStart: 4, paddingBlockStart: 6 } : null),
        ...(row ? { display: 'flex', gap: 4, flexWrap: 'wrap' } : null),
        ...(grid ? { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 } : null),
      }}>
      {(divided || row || grid) && <div style={groupLabelStyle} aria-hidden="true">{label}</div>}
      {children}
    </div>
  );
}

function Option({ selected, disabled = false, onSelect, tip, testId, children }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      title={tip}
      data-testid={testId}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'start',
        padding: '5px 7px', marginBottom: 2, borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12, color: 'rgba(255,255,255,0.92)', opacity: disabled ? 0.5 : 1,
        border: `1px solid ${selected ? 'rgba(255,255,255,0.7)' : 'transparent'}`,
        background: selected ? 'rgba(255,255,255,0.18)' : 'transparent',
      }}
    >
      <span style={{ flex: 1 }}>{children}</span>
      {selected && <span aria-hidden="true">✓</span>}
    </button>
  );
}

function Note({ children }) {
  return <p role="status" style={{ margin: '2px 7px 4px', fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{children}</p>;
}

const panelStyle = {
  position: 'absolute', bottom: '100%', insetInlineStart: 0, marginBottom: 6,
  minWidth: 190, maxWidth: 'min(320px, calc(100vw - 48px))', padding: 5, borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.25)',
  background: 'rgba(12,42,30,0.96)',
  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
};

const openDownward = { bottom: 'auto', top: '100%', marginBottom: 0, marginTop: 6, zIndex: 20 };

const groupLabelStyle = {
  gridColumn: '1 / -1', flexBasis: '100%', fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', padding: '0 7px 2px',
};

const linkStyle = {
  display: 'block', width: '100%', textAlign: 'start', padding: '4px 7px', fontSize: 11, cursor: 'pointer',
  color: 'rgba(255,255,255,0.85)', background: 'none', border: 0, textDecoration: 'underline',
};

const featuredStyle = {
  position: 'absolute', insetInlineStart: 3, insetBlockStart: 3, fontSize: 9, padding: '0 4px', borderRadius: 4,
  background: 'rgba(0,0,0,0.6)', color: '#fff',
};
